import { seasonDispatchDates, formatDate, dispatchDaysFrom } from '@theme/dispatch-date-math';

/*
 * Month-grid dispatch picker for "Peonies by Dispatch Date".
 *
 * Every date comes from seasonDispatchDates() in dispatch-date-math.js, and
 * the value written to properties[Dispatch Date] is formatDate(date), because
 * fulfilment reads that string. This file only owns the grid's appearance and
 * the variety-preference column beside it.
 *
 * Fail-open rules:
 *   - weekday list comes from the section settings; the section emits a
 *     Mon-Thu fallback in Liquid so this never receives a blank list
 *   - a missing or mistyped season end falls back to ~12 weeks (in the math
 *     module), so the picker is never silently empty
 *   - the <select> stays in the DOM, required and form-associated, so a
 *     no-JS visitor cannot post an order with no dispatch date on it
 */

const THURSDAY = 4;
const WEEKDAY_HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FALLBACK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday'];

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/* Monday-first column index for a JS day number. */
function columnFor(date) {
  return (date.getDay() + 6) % 7;
}

class DispatchCalendar extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-dispatch-date-select]');
    this.grid = this.querySelector('[data-dispatch-grid]');
    this.chosenLine = this.querySelector('[data-dispatch-chosen]');
    this.thursdayNote = this.querySelector('[data-dispatch-thursday]');
    this.closedNote = this.querySelector('[data-dispatch-closed]');
    this.closedDaysNote = this.querySelector('.dispatch-calendar__closed-note');
    this.aside = this.querySelector('.dispatch-calendar__aside');
    this.buyArea = this.querySelector('[data-dispatch-buy]');
    this.varietyInput = this.querySelector('[data-dispatch-variety-input]');
    this.varietyList = this.querySelector('[data-dispatch-variety-list]');
    this.varietyIntro = this.querySelector('[data-dispatch-variety-intro]');

    this.chosen = new Set();
    this.varieties = this.readVarieties();
    this.bindPriceSync();

    let days = dispatchDaysFrom(this.dataset.dispatchDays);
    if (!days.length) days = FALLBACK_DAYS;

    let dates = [];
    try {
      dates = seasonDispatchDates(this.dataset.seasonEnd, this.dataset.seasonStart || null, days);
    } catch (e) {
      dates = [];
    }

    // Days the farm does not dispatch (public holidays). Owner-editable list on
    // the section; parsed here so an excluded day can never be picked.
    const closed = new Set(
      (this.dataset.closedDates || '')
        .split(/[\s,]+/)
        .map((d) => d.trim())
        .filter(Boolean)
    );
    if (closed.size) dates = dates.filter(({ date }) => !closed.has(toISODate(date)));
    this.closedDates = closed;

    if (!dates.length) {
      this.renderClosed();
      return;
    }

    this.offered = new Map();
    dates.forEach(({ date, cutoff }) => {
      const iso = toISODate(date);
      this.offered.set(iso, {
        date,
        label: formatDate(date),
        cutoffLabel: `midnight ${formatDate(cutoff)}`,
      });
    });

    this.populateSelect();
    this.buildGrid();

    this.select.addEventListener('change', () => this.apply(this.select.selectedOptions?.[0]?.dataset.iso));
    this.grid.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-iso]');
      if (!button) return;
      this.apply(button.dataset.iso);
    });
    this.grid.addEventListener('keydown', (event) => this.onGridKeydown(event));

    if (this.varietyList) {
      this.varietyList.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-variety]');
        if (!button) return;
        this.toggleVariety(button.dataset.variety);
      });
    }

    this.apply([...this.offered.keys()][0]);
  }

  /* ---------------------------------------------------------------- price */

  /* Horizon only re-renders a price block whose block id matches the
     variant-picker's. Here they differ, so the price would sit on the first
     variant for ever. Read the server-rendered map and write the selected
     variant's price in ourselves. */
  bindPriceSync() {
    const script = this.querySelector('[data-dispatch-prices]');
    const buy = this.querySelector('[data-dispatch-buy]');
    if (!script || !buy) return;

    let prices;
    try {
      prices = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!prices || typeof prices !== 'object') return;

    const write = () => {
      const checked = buy.querySelector('input[type="radio"][data-variant-id]:checked');
      const price = checked && prices[checked.dataset.variantId];
      if (!price) return;
      buy.querySelectorAll('.price').forEach((el) => {
        el.textContent = price;
      });
    };

    // Written again after the picker's own morph, which replaces this markup.
    buy.addEventListener('change', () => {
      write();
      requestAnimationFrame(write);
      setTimeout(write, 400);
    });

    write();
  }

  readVarieties() {
    const script = this.querySelector('[data-dispatch-variety-windows]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /* ---------------------------------------------------------------- dates */

  populateSelect() {
    if (!this.select) return;
    let monthKey = null;
    let group = null;

    this.offered.forEach((entry, iso) => {
      const key = iso.slice(0, 7);
      if (key !== monthKey) {
        group = document.createElement('optgroup');
        group.label = entry.date.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
        this.select.appendChild(group);
        monthKey = key;
      }
      const option = document.createElement('option');
      option.value = entry.label;
      option.textContent = entry.label;
      option.dataset.iso = iso;
      group.appendChild(option);
    });

    this.select.classList.add('dispatch-calendar__select--replaced');
  }

  buildGrid() {
    if (!this.grid) return;
    this.grid.textContent = '';

    const isos = [...this.offered.keys()];
    const first = this.offered.get(isos[0]).date;
    const last = this.offered.get(isos[isos.length - 1]).date;

    let cursor = new Date(first.getFullYear(), first.getMonth(), 1);
    const end = new Date(last.getFullYear(), last.getMonth(), 1);

    while (cursor <= end) {
      this.grid.appendChild(this.buildMonth(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    this.grid.hidden = false;
  }

  buildMonth(monthStart) {
    const month = document.createElement('div');
    month.className = 'dispatch-calendar__month';

    const label = document.createElement('h3');
    label.className = 'dispatch-calendar__month-label';
    label.textContent = monthStart.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
    month.appendChild(label);

    const heads = document.createElement('div');
    heads.className = 'dispatch-calendar__heads';
    WEEKDAY_HEADS.forEach((name) => {
      const head = document.createElement('span');
      head.className = 'dispatch-calendar__head';
      head.setAttribute('aria-hidden', 'true');
      head.textContent = name;
      heads.appendChild(head);
    });
    month.appendChild(heads);

    const days = document.createElement('div');
    days.className = 'dispatch-calendar__days';

    for (let i = 0; i < columnFor(monthStart); i += 1) {
      const pad = document.createElement('span');
      pad.className = 'dispatch-calendar__pad';
      pad.setAttribute('aria-hidden', 'true');
      days.appendChild(pad);
    }

    const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
    for (let dayNum = 1; dayNum <= daysInMonth; dayNum += 1) {
      const date = new Date(monthStart.getFullYear(), monthStart.getMonth(), dayNum);
      const iso = toISODate(date);
      const entry = this.offered.get(iso);

      if (entry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dispatch-calendar__day dispatch-calendar__day--open';
        button.dataset.iso = iso;
        button.textContent = String(dayNum);
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', `${entry.label}. Order by ${entry.cutoffLabel}.`);
        days.appendChild(button);
      } else {
        const closed = document.createElement('span');
        const isHoliday = this.closedDates?.has(iso) === true;
        closed.className = `dispatch-calendar__day dispatch-calendar__day--closed${
          isHoliday ? ' dispatch-calendar__day--holiday' : ''
        }`;
        closed.textContent = String(dayNum);
        // A plain non-dispatch day is noise to a screen reader; a day the farm
        // is closed is a fact worth hearing, so it keeps its name.
        if (isHoliday) {
          closed.setAttribute('aria-label', `${dayNum}. Closed, we do not dispatch this day.`);
        } else {
          closed.setAttribute('aria-hidden', 'true');
        }
        days.appendChild(closed);
      }
    }

    month.appendChild(days);
    return month;
  }

  onGridKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const buttons = [...this.grid.querySelectorAll('button[data-iso]')];
    const index = buttons.indexOf(document.activeElement);
    if (index === -1) return;

    let next = index;
    if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);
    if (event.key === 'ArrowRight') next = Math.min(buttons.length - 1, index + 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;

    event.preventDefault();
    buttons[next].focus();
  }

  apply(iso) {
    const entry = iso ? this.offered.get(iso) : null;
    if (!entry) return;

    this.chosenIso = iso;

    // The <select> is the form control; the grid is only its skin.
    if (this.select && this.select.value !== entry.label) this.select.value = entry.label;

    this.grid.querySelectorAll('button[data-iso]').forEach((button) => {
      const on = button.dataset.iso === iso;
      button.classList.toggle('dispatch-calendar__day--selected', on);
      button.setAttribute('aria-pressed', String(on));
    });

    if (this.chosenLine) {
      this.chosenLine.textContent = `Leaves the farm ${entry.label}. Order by ${entry.cutoffLabel}.`;
    }

    if (this.thursdayNote) {
      this.thursdayNote.hidden = entry.date.getDay() !== THURSDAY;
    }

    this.renderVarieties(iso, entry.label);
  }

  /* ----------------------------------------------------------- varieties */

  expectedOn(iso) {
    return this.varieties.filter((v) => v.start && v.end && iso >= v.start && iso <= v.end);
  }

  renderVarieties(iso, dateLabel) {
    if (!this.varietyList) return;

    const expected = this.expectedOn(iso);
    const expectedNames = new Set(expected.map((v) => v.title));

    // Drop any preference that is not expected on the newly chosen date, so we
    // never send packing a name that was never offered for that run.
    [...this.chosen].forEach((name) => {
      if (!expectedNames.has(name)) this.chosen.delete(name);
    });

    if (this.varietyIntro) {
      this.varietyIntro.textContent = expected.length
        ? `Expected around ${dateLabel}`
        : `Nothing confirmed for ${dateLabel} yet. We pack what is at its best.`;
    }

    this.varietyList.textContent = '';

    expected.forEach((variety) => {
      const item = document.createElement('li');
      item.className = 'dispatch-calendar__variety';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dispatch-calendar__variety-button';
      button.dataset.variety = variety.title;
      button.setAttribute('aria-pressed', String(this.chosen.has(variety.title)));
      button.classList.toggle('dispatch-calendar__variety-button--selected', this.chosen.has(variety.title));

      if (variety.image) {
        const img = document.createElement('img');
        img.className = 'dispatch-calendar__variety-image';
        img.src = variety.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 200;
        img.height = 200;
        button.appendChild(img);
      } else {
        const blank = document.createElement('span');
        blank.className = 'dispatch-calendar__variety-image dispatch-calendar__variety-image--blank';
        blank.setAttribute('aria-hidden', 'true');
        button.appendChild(blank);
      }

      const name = document.createElement('span');
      name.className = 'dispatch-calendar__variety-name';
      name.textContent = variety.title;
      button.appendChild(name);

      item.appendChild(button);
      this.varietyList.appendChild(item);
    });

    this.writePreference();
  }

  toggleVariety(name) {
    if (this.chosen.has(name)) {
      this.chosen.delete(name);
    } else {
      this.chosen.add(name);
    }

    this.varietyList.querySelectorAll('button[data-variety]').forEach((button) => {
      const on = this.chosen.has(button.dataset.variety);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('dispatch-calendar__variety-button--selected', on);
    });

    this.writePreference();
  }

  writePreference() {
    if (!this.varietyInput) return;
    // Order follows the column, not click order, so the packing slip reads the
    // same way the customer saw it.
    const ordered = [...this.varietyList.querySelectorAll('button[data-variety]')]
      .map((button) => button.dataset.variety)
      .filter((name) => this.chosen.has(name));

    this.varietyInput.value = ordered.length ? ordered.join(', ') : 'Any available';
  }

  /* -------------------------------------------------------------- closed */

  renderClosed() {
    if (this.grid) this.grid.hidden = true;
    if (this.chosenLine) this.chosenLine.textContent = '';
    if (this.thursdayNote) this.thursdayNote.hidden = true;
    if (this.buyArea) this.buyArea.hidden = true;
    if (this.select) this.select.disabled = true;
    if (this.closedNote) this.closedNote.hidden = false;

    // Out of season the Labour Day note and the "tap any you would like"
    // aside describe a calendar that is not on the page.
    if (this.closedDaysNote) this.closedDaysNote.hidden = true;
    if (this.aside) this.aside.hidden = true;

    // A disabled select is skipped by validation as well as submission, so
    // required no longer blocks anything. Stop the form itself instead.
    if (this.buyArea) {
      this.buyArea
        .querySelectorAll('button[type="submit"], input[type="submit"], [name="add"]')
        .forEach((el) => {
          el.disabled = true;
        });
    }

    const field = this.querySelector('[data-dispatch-field]');
    if (field) field.hidden = true;
  }
}

if (!customElements.get('dispatch-calendar')) {
  customElements.define('dispatch-calendar', DispatchCalendar);
}
